<template>
    <div class="card mb-2">
        <div class="card-header pr-1 d-flex align-items-center">
            <span class="btnlike mr-auto">
                <h4 class="mb-0">Contact Information</h4>
            </span>
        </div>

        <div class="card-body">
            <alert-box type="caution" v-if="hasBouncedEmail">
                <strong>Bounced Email:</strong> Onboarding cannot proceed because email cannot be delivered.
                <a v-if="canEdit" href @click.prevent="editEmail">Update this user's email address to re-try.</a>
            </alert-box>

            <!-- Textfield with icon buttons to the right of it -->
            <div class="d-flex align-items-start">
                <div class="flex-fill overflow-auto">
                    <div class="mui-textfield">
                        <span class="textfield">{{ employee.email || (canEdit ? '-' : '') }}</span>
                        <label>Email Address</label>
                    </div>
                </div>
                <div class="flex-0">
                    <a v-if="employee.email" class="btn btn-outline-primary btn-collapse-xl" :href="'mailto:' + encodeURI(employee.email)">
                        <i class="far fa-envelope" aria-hidden="true" title="Send Email"></i>
                        <span class="btn-text">Send Email</span>
                    </a>
                    <button v-if="canEdit" class="btn btn-outline-primary btn-collapse-xl ml-hf" @click.prevent="editEmail">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        <span class="btn-text">Edit</span>
                    </button>
                </div>
            </div>

            <!-- NB: phone_home is for Employee; phone is for OnboardingApplication -->
            <div class="form-row">
                <div class="col-6">
                    <div class="mui-textfield" v-if="employee.phone_home_formatted">
                        <span class="textfield">{{ employee.phone_home_formatted }}</span>
                        <label>Phone</label>
                    </div>
                    <div class="mui-textfield" v-if="employee.phone_formatted">
                        <span class="textfield">{{ employee.phone_formatted }}</span>
                        <label>Phone</label>
                    </div>
                    <div class="mui-textfield" v-if="canEdit && !employee.phone_home_formatted && !employee.phone_formatted">
                        <span class="textfield">-</span>
                        <label>Phone</label>
                    </div>
                </div>
                <div v-if="canEdit" class="col-6">
                    <button class="btn btn-outline-primary btn-collapse-xl ml-hf" @click.prevent="editPhone">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        <span class="btn-text">Edit</span>
                    </button>
                </div>
            </div>

            <div class="form-row">
                <div class="col-6">
                    <div class="mui-textfield" v-if="employee.address_1">
                        <span class="textfield">
                            {{ employee.address_1 }}
                            <br />
                            {{ employee.address_2 }}
                            <br v-if="employee.address_2" />
                            {{ employee.city }}, {{ employee.state }} {{ employee.postal_code }}
                        </span>
                        <label>Resident Address</label>
                    </div>
                </div>
                <div v-if="canEdit" class="col-6">
                    <button class="btn btn-outline-primary btn-collapse-xl ml-hf" @click.prevent="editResidentAddress">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        <span class="btn-text">Edit</span>
                    </button>
                </div>
            </div>

            <div class="form-row">
                <div class="col-6">
                    <div class="mui-textfield" v-if="employee.address_1">
                        <span class="textfield">
                            <template v-if="employee.use_mailing_address">
                                {{ employee.mailing_address_1 }}
                                <br />
                                {{ employee.mailing_address_2 }}
                                <br v-if="employee.mailing_address_2" />
                                {{ employee.mailing_city }}, {{ employee.mailing_state }} {{ employee.mailing_postal_code }}
                            </template>
                            <template v-else>
                                Same as above
                            </template>
                        </span>
                        <label>Mailing Address</label>
                    </div>
                </div>
                <div v-if="canEdit" class="col-6">
                    <button class="btn btn-outline-primary btn-collapse-xl ml-hf" @click.prevent="editMailingAddress">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        <span class="btn-text">Edit</span>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <EditEmailModal
        :email="employee.email"
        :full-name="employee.full_name"
        :client="client"
        :employee-id="employee.is_employee ? employee.id : undefined"
        :onboarding-application-id="employee.is_onboarding_application ? employee.id : undefined"
        @updated="onUpdate"
        v-if="editEmailModalOpened"
        @close="editEmailModalOpened = false"
    />

    <EditPhoneModal
        :employee="employee"
        :client="client"
        v-if="editPhoneModalOpened"
        @updated="onUpdate"
        @close="editPhoneModalOpened = false"
    />

    <EditAddressModal
        :employee="employee"
        :client="client"
        :addressMode="addressMode"
        v-if="editAddressModalOpened"
        @updated="onUpdate"
        @close="editAddressModalOpened = false; addressMode = ''"
    />
</template>

<script>

import ButtonRow from '@/components/ButtonRow.vue'
import EditEmailModal from '@/components/EditEmployeeEmailModal'
import EditPhoneModal from './EditEmployeePhoneModal'
import EditAddressModal from './EditEmployeeAddressModal'

export default {
    props: ['employee', 'client'],
    emits: ['updated'],
    components: {ButtonRow, EditEmailModal, EditPhoneModal, EditAddressModal},
    data() {
        return {
            editEmailModalOpened: false,
            editPhoneModalOpened: false,
            editAddressModalOpened: false,
            addressMode: ''
        }
    },
    computed: {
        hasBouncedEmail() {
            if (!this.employee) {return false}
            return this.employee.warnings.filter(w => !w.resolved_on && w.warning_type == 'bounced-email').length > 0
        },
        canEdit() {
            if (this.employee.is_employee && !this.$permissions.hasRolesInCompany(this.client.id, this.employee.company_id, this.$store.state.security_roles.EMPLOYEE_ROLES)) {
                return false  // Onboarding-only managers can't edit existing employees' email addresses
            }
            return this.employee.is_employee || (this.employee.is_onboarding_application && this.employee.status != 'canceled')
        },
    },
    methods: {
        editEmail() {
            this.editEmailModalOpened = true
        },
        editPhone() {
            this.editPhoneModalOpened = true
        },
        editMailingAddress() {
            this.editAddressModalOpened = true
            this.addressMode = 'mailing'
        },
        editResidentAddress() {
            this.editAddressModalOpened = true
            this.addressMode = 'resident'
        },
        onUpdate() {
            this.$emit('updated')
        }
    },
}
</script>
